'use client';

import React, { useState, useMemo, useRef, Suspense } from 'react';
import { TopMoversSideWidget } from '../../../../_components/markets';

import { FreestarAd } from '@benzinga/ads';
import { useBenzingaEdge } from '@benzinga/edge';
import { HeatMapVisualization } from '@benzinga/heatmap-visualization';
import { createSymbolSearchItem } from '@benzinga/search-modules';
import { ThemeProvider } from '@benzinga/themetron';
import { WidgetContextProvider } from '@benzinga/widget-tools';

interface CapNode {
  name?: string;
  children?: CapNode[];
}

function getSymbolsFromCapTree(capData: CapNode[] = []): string[] {
  return capData
    .flatMap(sector => sector.children ?? [])
    .flatMap(sub => sub.children ?? [])
    .flatMap(sym => (sym.name ? [sym.name] : []));
}

interface HeatmapProps {
  smallCap: any[];
  midCap: any[];
  largeCap: any[];
  movers: any[];
  wiims: any[];
}

export function Heatmap({ smallCap, midCap, largeCap, movers, wiims }: HeatmapProps) {
  const [selectedChart, setSelectedChart] = useState<'large' | 'mid' | 'small'>('large');
  const [heatmapKey, setHeatmapKey] = useState(0);
  const { hasEdgeSubscription: hasBzEdge } = useBenzingaEdge();
  const widgetRef = useRef<HTMLDivElement | null>(null);

  const capData = useMemo(() => {
    switch (selectedChart) {
      case 'large':
        return largeCap;
      case 'mid':
        return midCap;
      case 'small':
        return smallCap;
    }
  }, [selectedChart, largeCap, midCap, smallCap]);

  const heatmapSources = useMemo(() => {
    const symbols = getSymbolsFromCapTree(capData);
    return symbols.map(s => createSymbolSearchItem(s));
  }, [capData]);

  React.useEffect(() => {
    const t = setTimeout(() => setHeatmapKey(1), 100);
    return () => clearTimeout(t);
  }, []);

  const containerStyle = {
    minHeight: 'calc(100vh - 200px)',
  };

  return (
    <div className="flex flex-col w-full justify-start bg-bzblue-1000 p-4" style={containerStyle}>
      <div className="w-full flex flex-col lg:flex-row gap-4">
        <div
          className="flex-1 lg:max-w-[calc(100vw-350px)]"
          style={{
            height: 'calc(100vh - 230px)',
          }}
        >
          <div className="h-full flex flex-col md:flex-row overflow-hidden">
            <div className="w-full md:w-80 p-2 text-white">
              <div className="p-2 md:p-4">
                <h2 className="text-xl font-bold text-white">Stock Heat Map</h2>
                <p className="text-xs">
                  Get to know how the market is trending as a whole using our heatmap. You can filter the map based on
                  market cap. Use the scroll wheel to zoom in and out. You can focus on a box by clicking on it.
                </p>
                <p className="pt-2 md:pt-10 font-bold">Filters:</p>
                <div className="flex flex-col gap-2 mt-2">
                  <button
                    className={`py-2 px-3 text-sm font-bold rounded-md text-left ${selectedChart === 'large' ? 'bg-bzblue-700 text-white' : 'bg-transparent text-white hover:bg-bzblue-800'}`}
                    onClick={() => setSelectedChart('large')}
                  >
                    Large Market Cap
                  </button>
                  <button
                    className={`py-2 px-3 text-sm font-bold rounded-md text-left ${selectedChart === 'mid' ? 'bg-bzblue-700 text-white' : 'bg-transparent text-white hover:bg-bzblue-800'}`}
                    onClick={() => setSelectedChart('mid')}
                  >
                    Mid Market Cap
                  </button>
                  <button
                    className={`py-2 px-3 text-sm font-bold rounded-md text-left ${selectedChart === 'small' ? 'bg-bzblue-700 text-white' : 'bg-transparent text-white hover:bg-bzblue-800'}`}
                    onClick={() => setSelectedChart('small')}
                  >
                    Small Market Cap
                  </button>
                </div>
              </div>

              {!hasBzEdge && (
                <div
                  className="hidden md:block w-full min-w-[300px] flex-shrink-0 mt-12"
                  style={{
                    height: 250,
                    maxHeight: 250,
                    minHeight: 250,
                  }}
                >
                  <FreestarAd placement="sidebar_1" />
                </div>
              )}
            </div>
            <div className="flex-1 min-h-[50vh] md:min-h-0 h-full" ref={widgetRef}>
              <ThemeProvider theme="dark">
                <WidgetContextProvider widgetRef={widgetRef.current ?? null}>
                  {heatmapSources.length > 0 ? (
                    <Suspense
                      fallback={
                        <div
                          className="h-full w-full flex items-center justify-center text-white"
                          style={{ color: '#e2e8f0', fontSize: 18 }}
                        >
                          Loading heatmap…
                        </div>
                      }
                    >
                      <HeatMapVisualization.visualization
                        key={heatmapKey}
                        args={{
                          ...HeatMapVisualization.defaultArgs,
                          emptyState: { noDataMessage: '' },
                        }}
                        flightMode={true}
                        onChange={() => {}}
                        onSourceChanged={() => {}}
                        sources={heatmapSources}
                      />
                    </Suspense>
                  ) : (
                    <div className="flex items-center justify-center h-full text-white">
                      No symbols in this market cap range.
                    </div>
                  )}
                </WidgetContextProvider>
              </ThemeProvider>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-[300px] flex-shrink-0 flex flex-col gap-4 ">
          {!hasBzEdge && (
            <div
              className="hidden lg:block w-full lg:w-[300px] flex-shrink-0"
              style={{
                height: 250,
                maxHeight: 250,
                minHeight: 250,
              }}
            >
              <FreestarAd placement="sidebar_1" />
            </div>
          )}

          <div className="min-h-0">
            <TopMoversSideWidget movers={movers ?? []} wiims={wiims ?? []} isToolsPage={true} />
          </div>

          {!hasBzEdge && (
            <div
              className="block lg:hidden w-full flex-shrink-0 flex justify-center"
              style={{
                height: 250,
                maxHeight: 250,
                minHeight: 250,
              }}
            >
              <div className="w-[320px] max-w-full">
                <FreestarAd placement="sidebar_1" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
